import styled from "@emotion/styled";
import { useMemo, useState } from "react";

import type { Product } from "@skilitics/billing-api-client/definition";
import {
  Button,
  ButtonGroup,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMI_BOLD,
  Icon,
  InfoTooltip,
  Skeleton,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFeatureSwitch } from "@skilitics/nautilus-core-designer";

import type { PlanMeta } from "../planMetadata";

export type PlanState = "loading" | "idle" | "updating";

export interface PlanCardProps {
  /**
   * Server state / live data.
   * In particular, the current subscription, if any;
   * and the current amount for prices.
   * Undefined if not loaded yet.
   */
  product: Product | undefined;
  /** Local metadata. Would be good to push more of this to server. */
  meta: PlanMeta;
  /** Transient state */
  state: PlanState;
  /** Request to change plan. */
  onChoosePlan: (priceKey: string | null) => void;
}

export const PlanCard = ({ product, meta, state, onChoosePlan }: PlanCardProps) => {
  const newPricesEnabled = useFeatureSwitch("new-billing");
  const subscription = product?.subscription;

  const prices = useMemo(() => {
    if (!meta.prices) {
      return null; // free
    }
    return meta.prices.filter((price) => newPricesEnabled || !price.needsFeatureSwitch);
  }, [meta.prices, newPricesEnabled]);

  // Only store user's manual selection (when they actively choose a different price)
  const [userSelectedKey, setUserSelectedKey] = useState<string | null>(null);

  // Derive the price key: use user selection if they made one, otherwise derive from props
  const priceKey = useMemo(() => {
    // If user has manually selected a price, use that
    if (userSelectedKey !== null) {
      return userSelectedKey;
    }

    // Otherwise, derive from props
    if (subscription?.planKey === meta.key) {
      return subscription.priceKey;
    } else if (prices) {
      return prices[0].key;
    } else {
      return null;
    }
  }, [userSelectedKey, subscription, meta.key, prices]);

  const handlePriceSelection = (selectedKey: string) => {
    setUserSelectedKey(selectedKey);
  };

  if (!product) {
    return <PlanLoadingCard />;
  }

  const selectedPrice = product.prices.find((p) => p.key === priceKey);
  const isActivePrice = subscription ? subscription.priceKey === priceKey : prices === null;

  return (
    <PlanContainer>
      <PlanName>{meta.name}</PlanName>
      {prices === null && <PlanPriceAmount>$0.00</PlanPriceAmount>}
      {prices !== null && <PlanPriceAmount>{selectedPrice?.amount ?? "-"}</PlanPriceAmount>}
      {prices !== null && !(prices.length > 1 && newPricesEnabled) && (
        <PlanInterval>Per {selectedPrice?.interval ?? "-"}</PlanInterval>
      )}
      {prices !== null && prices.length > 1 && newPricesEnabled && (
        <PlanPriceButtonList>
          {prices.map((priceMeta) => {
            const itemPrice = product.prices.find((p) => p.key === priceMeta.key);
            return (
              <PlanPriceButton
                key={priceMeta.key}
                variant={priceMeta.key === priceKey ? "contained" : "outlined"}
                onClick={() => handlePriceSelection(priceMeta.key)}
              >
                <PlanPriceInterval>Per {itemPrice?.interval}</PlanPriceInterval>
              </PlanPriceButton>
            );
          })}
        </PlanPriceButtonList>
      )}
      <PlanFeatureList>
        {meta.features.map((feature, index) => (
          <PlanFeature key={index}>
            <Checkbox>
              <Icon icon="check" />
            </Checkbox>
            <FeatureName>{feature.name}</FeatureName>
            {feature.description && <InfoTooltip tooltip={feature.description} />}
          </PlanFeature>
        ))}
      </PlanFeatureList>
      <Tooltip title={state === "updating" ? "Processing your existing plan change request" : ""}>
        {/* Can't put a tooltip onto a disabled Button it seems */}
        <div>
          <ChoosePlanButton
            disabled={isActivePrice || state !== "idle"}
            variant={isActivePrice ? "outlined" : "contained"}
            onClick={() => {
              onChoosePlan(priceKey);
            }}
          >
            {isActivePrice ? "Current Plan" : "Select Plan"}
          </ChoosePlanButton>
        </div>
      </Tooltip>
      {/* @ts-ignore the target missing from props. */}
      <Button href="https://www.skilitics.com/pricing#plancompare" target="_blank" rel="noopener noreferrer">
        View all features
      </Button>
    </PlanContainer>
  );
};

export const PlanLoadingCard = () => (
  <PlanContainer>
    <Skeleton variant="rounded" height={26} width={120} animation="wave" component={PlanName} />
    <Skeleton variant="rounded" height={38} animation="wave" component={PlanPriceAmount} />
    <PlanPriceInterval>
      <Skeleton variant="rounded" height={15} animation="wave" />
    </PlanPriceInterval>
    <PlanFeatureList>
      {[1, 2, 3].map((i) => (
        <PlanFeature key={i}>
          <Skeleton variant="rounded" height={15} width="100%" animation="wave" />
        </PlanFeature>
      ))}
    </PlanFeatureList>
    <Skeleton variant="rounded" height={34} animation="wave" />
  </PlanContainer>
);

const ChoosePlanButton = styled(Button)`
  width: 100%;
  justify-self: flex-end;
`;

const Checkbox = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  font-size: 10px;

  border-radius: 4px;
  background: ${({ theme }) => theme.onSurfaceInverse};
  color: ${({ theme }) => theme.onPrimary};
`;

const PlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 0 33%;
  padding: 4% 8%;
  max-width: 50%;

  &:not(:first-of-type) {
    border-left: solid 1px ${({ theme }) => theme.outlineSoft};
  }

  &:nth-of-type(1) {
    h4 {
      background: ${({ theme }) => theme.surfaceVariant};
    }

    ${ChoosePlanButton} {
      &:not(:disabled) {
        background: ${({ theme }) => theme.surfaceVariant};
        color: ${({ theme }) => theme.generalTextIcon};
      }
    }
  }

  &:nth-of-type(2) {
    h4 {
      background: ${({ theme }) => theme.primaryContainer};
      color: ${({ theme }) => theme.onPrimaryContainer};
    }

    ${ChoosePlanButton} {
      &:not(:disabled) {
        background: ${({ theme }) => theme.primary};
      }
    }

    ${Checkbox} {
      background: ${({ theme }) => theme.primary};
    }
  }

  &:nth-of-type(3) {
    h4 {
      background: ${({ theme }) => theme.secondaryContainer};
      color: ${({ theme }) => theme.onSecondaryContainer};
    }

    ${ChoosePlanButton} {
      &:not(:disabled) {
        background: ${({ theme }) => theme.secondary};
      }
    }

    ${Checkbox} {
      background: ${({ theme }) => theme.secondary};
    }
  }
`;

const PlanName = styled.h4`
  font-size: 12px;
  font-weight: ${FONT_WEIGHT_MEDIUM};
  padding: 6px 16px;
  border-radius: 8px;

  width: auto;
  align-self: flex-start;
  margin: 0 0 20px;
`;

const PlanPriceButtonList = styled(ButtonGroup)`
  justify-content: start;
`;

const PlanPriceButton = styled(Button)`
  display: flex;
  flex-direction: column;
`;

const PlanPriceAmount = styled.div`
  font-size: 32px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  margin: 0 0 5px;
`;

const PlanInterval = styled.div`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  color: ${({ theme }) => theme.generalTextIconVariant};
  margin: 0 0 15px;
`;

const PlanPriceInterval = styled.div`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const PlanFeatureList = styled.ul`
  flex: 1 1 auto;
  padding: 0;
  margin: 30px 0;
`;

const PlanFeature = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  list-style: none;

  margin: 0 0 8px;
`;

const FeatureName = styled.span`
  flex: 1 1 auto;
`;
